import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ExternalLink, ShieldCheck, Loader2, ArrowRight, Sparkles, RefreshCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import StripeConnectCard from "@/components/StripeConnectCard";
import { CONNECT_ENABLED } from "@/lib/featureFlags";
import { toast } from "sonner";
import { PriceDisplay, VatFootnote } from "@/lib/plan";

function priceFromCents(cents) {
  if (cents == null) return "—";
  const eur = cents / 100;
  return eur === Math.floor(eur) ? `${Math.floor(eur)}` : eur.toFixed(2);
}

function PlanCard({ plan, currentCode, cycle, onPick, submitting, has_annual_global }) {
  const isCurrent = currentCode === plan.code;
  const cycleData = plan?.cycles?.[cycle] || {};
  const monthly = plan?.cycles?.monthly || {};
  const annual = plan?.cycles?.annual || {};
  // Annual disabled => fall back to monthly display so the card never blanks.
  const showCycle = cycle === "annual" && !annual.available ? "monthly" : cycle;
  const showData = plan?.cycles?.[showCycle] || cycleData;
  const eur = priceFromCents(showData.price_cents ?? plan.price_cents);
  const periodLabel = showCycle === "annual" ? "/ year" : "/ month";
  const annualBadge = showCycle === "annual" && annual.is_promo;
  const annualSavings = (annual.savings_cents || 0) / 100;
  return (
    <div className={`bg-card border rounded-[10px] p-6 flex flex-col ${isCurrent ? "border-accent ring-2 ring-accent/40" : "border-border"}`} data-testid={`billing-plan-${plan.code}`}>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-accent">// {plan.code}</div>
          <div className="text-xl font-black mt-1">{plan.name}</div>
        </div>
        <div className="text-right">
          {/* Phase P — for monthly Pro, hoist the strike-through + special-offer
              display via the shared PriceDisplay. Falls back to the legacy
              raw price for annual/other plans where the cycle-price is dynamic. */}
          {showCycle === "monthly" ? (
            <PriceDisplay code={plan.code} size="lg" hideCadence className="justify-end" />
          ) : (
            <>
              <div className="text-3xl font-black">€{eur}</div>
              <div className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                {periodLabel} · + VAT
              </div>
            </>
          )}
          {showCycle === "monthly" && (
            <div className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{periodLabel}</div>
          )}
        </div>
      </div>
      {annualBadge && (
        <div
          className="mt-3 flex items-start gap-2 rounded-md border border-amber-400/60 bg-amber-50/70 dark:bg-amber-900/30 px-3 py-2 text-[12px] text-amber-900 dark:text-amber-200"
          data-testid={`billing-annual-promo-${plan.code}`}
        >
          <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <b>Promotional — Pro at Basic price.</b>
            {annualSavings > 0 && (
              <> Save €{Math.round(annualSavings)} vs paying monthly.</>
            )}
          </div>
        </div>
      )}
      {showCycle === "annual" && monthly.price_cents != null && (
        <div className="mt-2 text-[11px] mono uppercase tracking-wider text-muted-foreground">
          Equivalent to €{priceFromCents(Math.round((showData.price_cents || 0) / 12))} / month
        </div>
      )}
      <ul className="mt-4 space-y-1.5 text-sm flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex gap-2 items-start"><CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />{f}</li>
        ))}
      </ul>
      <div className="mt-5">
        {isCurrent ? (
          <Button disabled className="w-full" data-testid={`billing-current-${plan.code}`}>Current plan</Button>
        ) : (
          <Button
            className="w-full btn-cta"
            disabled={submitting || (cycle === "annual" && !annual.available)}
            onClick={() => onPick(plan.code, showCycle)}
            data-testid={`billing-upgrade-${plan.code}`}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
            {currentCode === "trial" ? "Upgrade now" : "Switch plan"}
          </Button>
        )}
      </div>
      {cycle === "annual" && !annual.available && (
        <div className="mt-2 text-[11px] text-muted-foreground italic" data-testid={`billing-annual-unavailable-${plan.code}`}>
          Annual pricing for {plan.name} not configured yet — contact support.
        </div>
      )}
    </div>
  );
}

function CycleToggle({ value, onChange, anyAnnual }) {
  if (!anyAnnual) return null;
  return (
    <div className="inline-flex rounded-full border border-border bg-card p-1 gap-1" role="tablist" data-testid="billing-cycle-toggle">
      <button
        type="button"
        role="tab"
        aria-selected={value === "monthly"}
        onClick={() => onChange("monthly")}
        className={`px-4 py-1.5 text-sm rounded-full transition-colors ${value === "monthly" ? "bg-accent text-accent-foreground font-bold" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="billing-cycle-monthly"
      >
        Monthly
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "annual"}
        onClick={() => onChange("annual")}
        className={`px-4 py-1.5 text-sm rounded-full transition-colors flex items-center gap-1.5 ${value === "annual" ? "bg-accent text-accent-foreground font-bold" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="billing-cycle-annual"
      >
        Annual <span className="mono text-[10px] uppercase tracking-wider bg-amber-300 text-amber-950 rounded px-1.5">Save</span>
      </button>
    </div>
  );
}

export default function Billing() {
  const [sub, setSub] = useState(null);
  const [plansData, setPlansData] = useState(null);
  const [discount, setDiscount] = useState(null);  // Phase L
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, plansRes, discRes] = await Promise.all([
        api.get("/billing/subscription"),
        api.get("/billing/plans"),
        api.get("/subscription/discount").catch(() => ({ data: null })),
      ]);
      setSub(subRes.data);
      setPlansData(plansRes.data);
      setDiscount(discRes.data || null);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Phase P.14 — auto-sync guard. If the tenant currently reads as
  // "Free trial" (or has a stripe_customer_id but no active plan_code),
  // silently call /billing/sync-subscription and reload once. Fixes the
  // "paid on Stripe, app still shows trial" webhook-drift bug.
  const [autoSyncTried, setAutoSyncTried] = useState(false);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => {
    if (autoSyncTried || loading || !sub) return;
    const looksStuck =
      (sub.plan_code === "trial" || !sub.plan_code) && !!sub.stripe_customer_id;
    if (!looksStuck) return;
    setAutoSyncTried(true);
    (async () => {
      try {
        const { data } = await api.post("/billing/sync-subscription", {});
        if (data?.ok) {
          toast.success("Subscription updated from Stripe");
          load();
        }
      } catch (_) {
        // Silent — this is a background heal, not a user action.
      }
    })();
  }, [sub, loading, autoSyncTried, load]);

  const refreshFromStripe = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/billing/sync-subscription", {});
      if (data?.ok) {
        toast.success(`Plan synced from Stripe: ${data?.applied?.plan_code || "updated"}`);
        load();
      } else {
        // Human-readable message when Stripe has nothing to tell us.
        const reason = data?.reason || "no_change";
        toast.info(
          reason === "no_stripe_customer"
            ? "No Stripe customer on this tenant yet — start a checkout first."
            : reason === "no_subscriptions"
            ? "Stripe reports no active subscription on this customer."
            : data?.message || "Nothing to sync."
        );
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      toast.success("Payment complete. Your plan will update shortly.");
      const next = new URLSearchParams(params);
      next.delete("session_id"); next.delete("stub"); next.delete("plan"); next.delete("cycle");
      setParams(next, { replace: true });
      setTimeout(load, 1200);
    }
    if (params.get("canceled")) {
      toast.info("Checkout canceled.");
      const next = new URLSearchParams(params); next.delete("canceled");
      setParams(next, { replace: true });
    }
  }, [params, setParams, load]);

  const checkout = async (plan, chosenCycle) => {
    setSubmitting(plan);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan,
        billing_cycle: chosenCycle || cycle,
        origin_url: window.location.origin,
      });
      window.location.assign(data.checkout_url);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      setSubmitting("");
    }
  };

  const openPortal = async () => {
    setSubmitting("portal");
    try {
      const { data } = await api.post("/billing/portal", {});
      if (data.stub) {
        toast.info("Billing portal is in test mode — no Stripe customer yet.");
        setSubmitting("");
        return;
      }
      window.location.assign(data.portal_url);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      setSubmitting("");
    }
  };

  if (loading || !sub) {
    return <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-14 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading billing…</div>;
  }

  const planLabel = { trial: "Free trial", basic: "Basic", pro: "Pro", custom: "Custom" }[sub.plan_code] || sub.plan_name;
  const trialLeft = sub.trial_days_remaining;
  const enrichedPlans = (plansData?.plans) || [];
  const anyAnnual = !!(plansData?.has_annual_basic || plansData?.has_annual_pro);

  return (
    <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-10" data-testid="billing-page">
      <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">// Billing</div>
      <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Subscription</h1>
      <p className="text-sm text-muted-foreground mt-1">Manage your JobsBook plan, update payment details, or cancel anytime.</p>

      {/* Phase 33 / P.10 — per-tenant Stripe Connect onboarding.
          Feature-gated via `lib/featureFlags::CONNECT_ENABLED`; backend
          endpoints stay intact so re-enabling is a one-line flag flip. */}
      {CONNECT_ENABLED && (
        <div className="mt-6">
          <StripeConnectCard />
        </div>
      )}

      {/* Phase L — tenant-facing discount banner. Lives above the plan
          card so the manager sees it on first scroll. */}
      {discount && (
        <div
          className="mt-6 bg-amber-50 border border-amber-200 text-amber-900 rounded-[10px] p-4 flex items-start gap-3"
          data-testid="tenant-discount-banner"
        >
          <div className="text-2xl">🎟️</div>
          <div className="flex-1">
            <div className="text-xs mono uppercase tracking-widest text-amber-700/80">Promo applied</div>
            <div className="text-lg font-extrabold mt-0.5">
              {discount.type === "percent"
                ? `${Number(discount.value).toFixed(0)}% off ${discount.duration === "repeating" ? `for ${discount.duration_in_months} months` : discount.duration === "forever" ? "forever" : "once"}`
                : `€${Number(discount.value).toFixed(2)} off ${discount.duration === "repeating" ? `for ${discount.duration_in_months} months` : discount.duration === "forever" ? "forever" : "once"}`}
            </div>
            {discount.reason && <div className="text-xs italic mt-0.5">&ldquo;{discount.reason}&rdquo;</div>}
          </div>
        </div>
      )}

      {/* Phase P.29 — 30-day trial extension messaging. Shown only for
          tenants still in `trial` plan so paying customers don't see it.
          Copy nudges them to add a card DURING the trial to unlock the
          full 30-day runway. */}
      {sub.plan_code === "trial" && (
        <div
          className="mt-6 bg-[#F0FFFA] border-2 border-[#8FFFE0] rounded-[10px] p-5 flex items-start gap-4"
          data-testid="trial-extend-banner"
        >
          <div className="text-3xl">🎁</div>
          <div className="flex-1">
            <div className="mono text-[11px] uppercase tracking-widest text-[#03313A] font-bold">
              Add a card now — get 30 days free
            </div>
            <div className="text-lg font-extrabold mt-1 text-[#03313A]">
              Save the card, extend your trial from 7 days to <span className="text-[#03313A] underline underline-offset-4 decoration-[#8FFFE0] decoration-[3px]">30 days</span>.
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Your card won&rsquo;t be charged until day 30. Cancel anytime before then and pay nothing.
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 bg-card border border-border rounded-[10px] p-6 flex items-center justify-between flex-wrap gap-4" data-testid="billing-current-card">
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Current plan</div>
          <div className="mt-1 text-2xl font-black flex items-center gap-2">
            {planLabel}
            {sub.billing_cycle && (
              <span className="mono text-[10px] uppercase tracking-wider bg-secondary text-secondary-foreground border border-border px-1.5 py-0.5 rounded" data-testid="billing-current-cycle">
                {sub.billing_cycle}
              </span>
            )}
          </div>
          {sub.plan_code === "trial" && trialLeft !== null && (
            <div className="mt-1 text-sm text-accent mono">Trial ends in {Math.max(0, trialLeft)} day{trialLeft === 1 ? "" : "s"} ({new Date(sub.trial_expires_at).toLocaleDateString()})</div>
          )}
          {sub.plan_code !== "trial" && sub.current_period_end && (
            <div className="mt-1 text-sm text-muted-foreground">Next period ends {new Date(sub.current_period_end).toLocaleDateString()}</div>
          )}
          {sub.stripe_subscription_id && (
            <div className="mt-1 mono text-[11px] text-muted-foreground">Stripe sub: {sub.stripe_subscription_id.slice(0, 20)}…</div>
          )}
        </div>
        {sub.stripe_customer_id && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={refreshFromStripe}
              disabled={syncing}
              data-testid="billing-refresh-btn"
              title="Re-fetch subscription state from Stripe"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
              Refresh status
            </Button>
            <Button variant="outline" onClick={openPortal} disabled={submitting === "portal"} data-testid="billing-portal-btn">
              {submitting === "portal" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Manage billing in Stripe
            </Button>
          </div>
        )}
      </div>

      {!sub.billing_configured && (
        <div className="mt-4 border border-amber-500 bg-amber-50 text-amber-900 rounded-md p-3 text-sm flex items-start gap-2" data-testid="billing-stub-notice">
          <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">Billing is in test mode.</div>
            <div>Upgrade buttons will simulate a checkout. To wire real Stripe, set <code className="mono">STRIPE_SECRET_KEY</code>, <code className="mono">STRIPE_WEBHOOK_SECRET</code>, <code className="mono">STRIPE_PRICE_BASIC</code> and <code className="mono">STRIPE_PRICE_PRO</code> in the backend env.</div>
          </div>
        </div>
      )}

      <div className="mt-10 flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-xl font-extrabold tracking-tight">Compare plans</h2>
        <CycleToggle value={cycle} onChange={setCycle} anyAnnual={anyAnnual} />
      </div>
      {sub.plan_code === "custom" ? (
        <div
          className="mt-4 border border-border bg-secondary/40 rounded-md p-4 text-sm flex items-start gap-3"
          data-testid="billing-custom-plan-notice"
        >
          <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-accent" />
          <div>
            <div className="font-bold">You're on a Custom plan managed by the platform admin.</div>
            <div className="text-muted-foreground mt-1">
              Upgrade and switch-plan options are disabled for Custom tenants.
              To change pricing or billing, contact{" "}
              <a
                href={`mailto:${sub.platform_contact_email || "info@jobsbook.app"}`}
                className="text-accent underline-offset-2 hover:underline"
              >
                {sub.platform_contact_email || "info@jobsbook.app"}
              </a>
              .
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
          {enrichedPlans.map((p) => (
            <PlanCard
              key={p.code}
              plan={p}
              currentCode={sub.plan_code}
              cycle={cycle}
              onPick={checkout}
              submitting={submitting === p.code}
              has_annual_global={anyAnnual}
            />
          ))}
        </div>
      )}

      <VatFootnote />
      <p className="mt-2 text-[11px] mono uppercase tracking-[0.18em] text-muted-foreground">
        {anyAnnual ? "Choose monthly or annual." : "Charged monthly."} Cancel any time from the Stripe Customer Portal.
      </p>

      <div className="mt-10 flex items-center gap-3">
        <Button asChild variant="outline"><Link to="/calendar" data-testid="billing-back-calendar">← Back to calendar</Link></Button>
      </div>
    </div>
  );
}
